import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@peacecover/component';
import { ArrowLeft } from '@peacecover/icon';
import { useGetCastRelationships } from '../../domains/scene/ui/hooks/useGetCastRelationships';
import { useGetBettieBalances } from '../../domains/bettie/ui/hooks/useGetBettieBalances';
import { usePaymentSuccessMessage } from '../hooks/usePaymentSuccessMessage';
import { useCurrentCastRelationship } from './hooks/useCurrentCastRelationship';
import { useUpdateThirdPersona } from './hooks/useUpdateThirdPersona';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import PreviousCastRelationshipList from './components/PreviousCastRelationshipList';
import CurrentCastRelationshipSection, {
  DoubleArrow
} from './components/CurrentCastRelationshipSection';
import GradientLoadingSpinner from '../components/GradientLoadingSpinner';
import CreateCastRelationshipBottomSheet from './components/CreateCastRelationshipBottomSheet';
import ThirdPersonaInputStep from './components/ThirdPersonaInputStep';
import { ROUTES } from '../../constants/routes';
import { BETTIE_QUERY_KEYS } from '../../domains/bettie/ui/queryKeys';

const CastRelationshipsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const { castRelationships, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetCastRelationships({
      perPage: 5
    });
  const { currentCastRelationship, previousCastRelationships } =
    useCurrentCastRelationship(castRelationships);
  const {
    isOpen: isChangeThirdPersonaOpen,
    open: openChangeThirdPersona,
    close: closeChangeThirdPersona,
    thirdPersona,
    setThirdPersonaName,
    setThirdPersonaGender,
    handleUpdate: handleUpdateThirdPersona,
    isUpdating
  } = useUpdateThirdPersona({ currentCastRelationship });

  const { data: bettieBalances } = useGetBettieBalances();
  const hasCastRelationshipTicket = (bettieBalances?.castRelationshipTicket?.balance ?? 0) > 0;

  const handleCreateCastRelationship = () => {
    if (!hasCastRelationshipTicket) {
      setIsOpen(true);
      return;
    }
    navigate(ROUTES.SCENE.SETTING);
  };

  useEffect(() => {
    if (castRelationships?.length <= 1 && !isLoading) {
      setIsOpen(true);
    }
  }, [castRelationships?.length, isLoading]);

  usePaymentSuccessMessage({
    onPaymentSuccess: () => {
      queryClient.invalidateQueries(BETTIE_QUERY_KEYS.bettieBalances);
      navigate(ROUTES.SCENE.SETTING);
    }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background-dark">
        <GradientLoadingSpinner size={36} />
      </div>
    );
  }

  if (isChangeThirdPersonaOpen) {
    return (
      <ThirdPersonaInputStep
        thirdPersonaName={thirdPersona.name}
        thirdPersonaGender={thirdPersona.gender}
        onThirdPersonaNameChange={setThirdPersonaName}
        onThirdPersonaGenderChange={setThirdPersonaGender}
        onBack={closeChangeThirdPersona}
        onNext={handleUpdateThirdPersona}
        isLoading={isUpdating}
        isUpdate={true}
      />
    );
  }

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[calc(env(safe-area-inset-bottom)+100px)] bg-background-dark">
      <Header />
      <div className="px-[24px] py-[10px] mt-[10px] flex flex-col gap-[14px] items-start">
        <h1 className="text-[26px] font-bold leading-[30px] text-main-dark-1 whitespace-pre-line">
          {t('CastRelationship.title')}
        </h1>
        <p className="text-[15px] font-medium leading-[18px] text-main-dark-4 ">
          {t('CastRelationship.description')}
        </p>
      </div>
      <div className="mt-[33px]">
        <CurrentCastRelationshipSection
          currentCastRelationship={currentCastRelationship ?? undefined}
        >
          <div className="flex flex-col items-center gap-[12px]">
            <DoubleArrow />
            <div className="px-[8px] py-[4px] bg-[linear-gradient(47deg,rgba(69,220,174,0.30)_11.79%,rgba(93,62,250,0.30)_99.08%)] rounded-[30px] text-[11px] font-semibold leading-[13px]">
              <span className="bg-[linear-gradient(47deg,#45DCAE_0%,#5D3EFA_99.08%)] bg-clip-text text-transparent">
                {t('CastRelationship.current')}
              </span>
            </div>
          </div>
        </CurrentCastRelationshipSection>
      </div>
      {previousCastRelationships.length > 0 ? (
        <PreviousCastRelationshipList
          hasCastRelationshipTicket={hasCastRelationshipTicket}
          castRelationships={previousCastRelationships}
          currentCastRelationship={currentCastRelationship ?? undefined}
          handleCreateCastRelationship={handleCreateCastRelationship}
          onOpenChangeThirdPersona={openChangeThirdPersona}
          onEndReached={() => {
            if (!hasNextPage || isFetchingNextPage) return;
            fetchNextPage();
          }}
        />
      ) : (
        <EmptyCase
          onClick={handleCreateCastRelationship}
          onOpenChangeThirdPersona={openChangeThirdPersona}
          thirdPersonaName={currentCastRelationship?.personC?.name ?? ''}
          hasCastRelationshipTicket={hasCastRelationshipTicket}
        />
      )}
      <CreateCastRelationshipBottomSheet isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </div>
  );
};

export default CastRelationshipsPage;

const Header = () => {
  const { t } = useTranslation();
  const handleHistoryBack = useHandleHistoryBack();
  return (
    <header className="flex items-center justify-between px-[16px] py-[8px] relative">
      <button type="button" onClick={handleHistoryBack}>
        <ArrowLeft size={24} className="text-main-dark-1" />
      </button>
      <p className="text-[17px] font-bold leading-[20px] text-main-dark-1 absolute left-1/2 transform -translate-x-1/2 whitespace-nowrap">
        {t('CastRelationship.pageTitle')}
      </p>
    </header>
  );
};

const EmptyCase = ({
  onClick,
  onOpenChangeThirdPersona,
  thirdPersonaName,
  hasCastRelationshipTicket
}: {
  onClick: () => void;
  onOpenChangeThirdPersona: () => void;
  thirdPersonaName: string;
  hasCastRelationshipTicket: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className="mt-[72px] flex flex-col items-center px-[24px] w-full">
      <div className="flex flex-col items-center gap-[16px]">
        <p className="text-[50px]">😳</p>
        <p className="text-[17px] font-bold leading-[20px] text-main-dark-2">
          {t('CastRelationship.emptyTitle')}
        </p>
      </div>
      <div className="fixed bottom-0 left-0 right-0 px-[24px] pb-[calc(env(safe-area-inset-bottom)+36px)] flex flex-col items-center">
        <button
          type="button"
          className="mt-[20px] text-[12px] font-medium leading-[14px] text-main-dark-3"
          onClick={onOpenChangeThirdPersona}
        >
          {t('CastRelationship.thirdPersona', { nickname: thirdPersonaName || '-' })}
          <span
            className="ml-[8px] underline leading-[14px]"
            style={{
              textUnderlineOffset: '2px'
            }}
          >
            {t('CastRelationship.change')}
          </span>
        </button>
        <Button
          size="large"
          className="w-full text-[17px] font-semibold mt-[24px]"
          onClick={onClick}
        >
          {hasCastRelationshipTicket
            ? t('CastRelationship.useTicket')
            : t('CastRelationship.emptyStartButton')}
        </Button>
      </div>
    </div>
  );
};
